import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Grid } from 'semantic-ui-react';
import styled from 'styled-components';
import { RouteComponentProps, useHistory, useLocation } from 'react-router';
import { LoginAndRegistrationContainer } from 'containers/LoginAndRegistrationContainer';
import { Footer } from 'components/molecules/Footer';
import { TwoColumn, PageContainer } from 'components/templates';
import { PreviousPage } from 'components/organisms/PreviousPage/PreviousPage';
import { AicpaLogo, Spacer, Heading, NotificationBanner, NotificationBannerEnums, Loader } from 'components/atoms';
import { Scroller, getPath } from 'utils';
import backgroundImage from 'resources/images/illustrations-build-feed.svg';
import { HelpSection } from 'components/molecules/LoginAndRegistration/LoginAndRegistration';
import { Routes } from 'constants/index';
import { ReactComponent as IconError } from 'resources/images/ic-error.svg';
import { isCenterMembershipJourneySelector } from 'modules/membership/selectors';
import { Utils } from 'mxp-utils';
import { getAuthHelper } from 'utils/auth';
import {
  ConsumerAuthHelper,
  OKTA_ASSOCIATION_REDIRECT_URL,
  OKTA_POST_LOGIN_REDIRECT,
  OKTA_PRESERVE_POST_SIGNUP_REDIRECT,
} from 'utils/auth/consumer';
import { usePreviousRoute } from 'hooks/usePreviousRoute';

interface Props extends RouteComponentProps {
  userPasswordUpdated: boolean;
  cleanup: () => void;
}

const loginRoutes = [getPath(Routes.LOGIN), getPath(Routes.LOGIN_HASH)];
const loginHashRoute = getPath(Routes.LOGIN_HASH);

const stripHash = (route: string): string => route.split('#')[0];

const appendHashToRoute = (route: string, hash?: string): string => {
  if (!hash || route.includes('#')) {
    return route;
  }
  return `${route}${hash}`;
};

const isLoginRoute = (route: string): boolean => {
  const routeWithoutHash = stripHash(route);
  const routePathOnly = routeWithoutHash.split('?')[0];
  const routeHasLoginHash = route === loginHashRoute || route.endsWith(loginHashRoute);

  return (
    loginRoutes.includes(route) ||
    loginRoutes.includes(routeWithoutHash) ||
    routePathOnly === getPath(Routes.LOGIN) ||
    (routeHasLoginHash && ['', getPath(Routes.ROOT), getPath(Routes.LOGIN)].includes(routePathOnly))
  );
};

// The public homepage isn't a valid post-login destination: it should never trap a
// newly-authenticated user, who is expected to land on the feed like everyone else.
const isHomeRoute = (route: string): boolean => {
  const routePathOnly = stripHash(route).split('?')[0];
  return routePathOnly === getPath(Routes.HOME, { withoutParams: true });
};

const getValidRedirectRoute = (routeToRedirect?: string | null, prevRoute?: string | null): string | null => {
  const redirectRoute = routeToRedirect || prevRoute;
  return redirectRoute && !isLoginRoute(redirectRoute) && !isHomeRoute(redirectRoute) ? redirectRoute : null;
};

type PageLoginRedirectMode = 'login' | 'signup';

interface RegistrationPrefill {
  inviteId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface PageLoginRedirectLocationState {
  email?: string;
  prevRoute?: string;
  hash?: string;
  registrationPrefill?: RegistrationPrefill;
}

const TEST_AICPA_HOST_SUFFIX = '.test-aicpa.org';

const getAppSourceFromHostname = (hostname: string): string | undefined => {
  const normalizedHostname = hostname.trim().toLowerCase();

  if (!normalizedHostname) return undefined;

  if (normalizedHostname.endsWith(TEST_AICPA_HOST_SUFFIX)) {
    const subdomain = normalizedHostname.slice(0, -TEST_AICPA_HOST_SUFFIX.length).trim();
    return subdomain || undefined;
  }

  return undefined;
};

const normalizeAppSource = (appSource?: string | null): string | undefined => {
  const normalizedAppSource = appSource?.trim().toLowerCase();
  return normalizedAppSource || undefined;
};

export const PageLoginRedirect: React.FC<{
  routeToRedirect?: string | null;
  authMode?: PageLoginRedirectMode;
}> = ({ routeToRedirect = null, authMode = 'login' }) => {
  const prevRoute = usePreviousRoute();
  const location = useLocation<PageLoginRedirectLocationState>();

  useEffect(() => {
    const prevRouteWithHash = appendHashToRoute(prevRoute, location.state?.hash);
    const redirectRoute = getValidRedirectRoute(routeToRedirect, prevRouteWithHash);
    const registrationPrefill = authMode === 'signup' ? location.state?.registrationPrefill : undefined;
    const loginHint = location.state?.email || registrationPrefill?.email || '';
    const urlParams = new URLSearchParams(location.search);
    const associationRedirectUrl =
      authMode === 'signup'
        ? Utils.getValidatedAssociationRedirectUrl(urlParams.get('association_redirect_url') || '')
        : undefined;
    const appSource =
      authMode === 'signup'
        ? normalizeAppSource(urlParams.get('app_source')) || getAppSourceFromHostname(window.location.hostname)
        : undefined;

    if (redirectRoute) sessionStorage.setItem(OKTA_POST_LOGIN_REDIRECT, redirectRoute);

    sessionStorage.removeItem(OKTA_ASSOCIATION_REDIRECT_URL);
    sessionStorage.removeItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT);
    if (authMode === 'signup' && associationRedirectUrl) {
      sessionStorage.setItem(OKTA_ASSOCIATION_REDIRECT_URL, associationRedirectUrl);
    }
    if (authMode === 'signup' && registrationPrefill?.inviteId) {
      sessionStorage.setItem(OKTA_PRESERVE_POST_SIGNUP_REDIRECT, 'true');
    }

    // Trigger Okta-hosted redirect login
    const authHelper = getAuthHelper();

    if (authHelper instanceof ConsumerAuthHelper) {
      authHelper.redirectToHostedLoginWidget({
        loginHint,
        navigationMode: 'replace',
        ...(authMode === 'signup'
          ? {
              action: 'signup',
              appSource,
              associationRedirectUrl,
              firmBillingEmail: registrationPrefill?.email,
              firmBillingFirstName: registrationPrefill?.firstName,
              firmBillingLastName: registrationPrefill?.lastName,
            }
          : {}),
      });
    }
  }, [authMode, location.search, location.state, prevRoute, routeToRedirect]);
  return <Loader active content="Loading" />;
};

export const PageSignupRedirect: React.FC = () => <PageLoginRedirect authMode="signup" />;

export const PageLogin: React.FC<Props> = ({ userPasswordUpdated, cleanup }) => {
  const history = useHistory();
  const isCenterMembershipJourney = useSelector(isCenterMembershipJourneySelector);

  React.useEffect(() => {
    Scroller.scrollToTop();
    return cleanup;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoClick = React.useCallback(() => {
    history.push(getPath(Routes.ROOT));
  }, [history]);

  return (
    <div>
      <PageContainer>
        <StyledHeader>
          <LogoContainer>
            <AicpaLogo onClick={handleLogoClick} />
          </LogoContainer>
        </StyledHeader>
        <Container>
          {!userPasswordUpdated && (
            <StyledButtonContainer>
              <PreviousPage backText="Back to previous page" />
            </StyledButtonContainer>
          )}
          <TwoColumn>
            <StyledContainer>
              <>
                <StyledHeading>Log in or create an account</StyledHeading>
                <StyledSubHeading>To ensure your data is saved we are creating an account for you</StyledSubHeading>
                {isCenterMembershipJourney && (
                  <StyledNotificationBanner
                    testId="redirect-center-membership-login"
                    childrenTestId="redirect-center-membership-login"
                    variant={NotificationBannerEnums.variant.blue}
                    icon={<StyledIconError />}
                  >
                    To proceed, you will need to log in as a partner/owner or equivalent, and an active AICPA Regular
                    Member. (Note: this is not a requirement for CPEA)
                  </StyledNotificationBanner>
                )}
              </>
              <LoginAndRegistrationContainer isModal={false} />
              <Spacer />
            </StyledContainer>

            <StyledHeadlineContainer data-testid="right-block-headline">
              <Headline>
                <BoldHeadline>
                  Welcome to our new, consolidated website for AICPA<sup>®</sup> & CIMA<sup>®</sup> members.
                </BoldHeadline>
                <SubHeadline>If you haven't already, please log in:</SubHeadline>
              </Headline>

              <Grid columns={2}>
                <Grid.Row>
                  <Grid.Column computer={1} mobile={2}>
                    1.
                  </Grid.Column>
                  <Grid.Column computer={13} mobile={13}>
                    <StyledParagraph>Select "Forgotten password".</StyledParagraph>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column computer={1} mobile={2}>
                    2.
                  </Grid.Column>
                  <Grid.Column computer={13} mobile={13}>
                    <StyledParagraph>Enter your email address.</StyledParagraph>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column computer={1} mobile={2}>
                    3.
                  </Grid.Column>
                  <Grid.Column computer={13} mobile={13}>
                    <StyledParagraph>Click "Send reset password email".</StyledParagraph>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column computer={1} mobile={2}>
                    4.
                  </Grid.Column>
                  <Grid.Column computer={13} mobile={13}>
                    <StyledParagraph>Check your email and follow the instructions.</StyledParagraph>
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              <StyledImageContainer>
                <StyledImage data-testid="right-block-image" />
              </StyledImageContainer>
            </StyledHeadlineContainer>
          </TwoColumn>
        </Container>
        <StyledHelpContainer>
          <HelpSection className="Help-Section" />
        </StyledHelpContainer>
        <div>
          <Footer />
        </div>
      </PageContainer>
    </div>
  );
};

const StyledHeader = styled.header`
  box-shadow: 0 ${props => props.theme.pxToRem(2)} ${props => props.theme.pxToRem(6)} 0 rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.97);
  height: ${props => props.theme.pxToRem(80)};
  margin-bottom: 2rem;
`;

const StyledHeading = styled(Heading)`
  &&& {
    font-size: ${props => props.theme.fontSizes.xl};
    color: ${props => props.theme.colors.neutralGrey8};
    font-weight: ${props => props.theme.fontWeights.light};
  }
`;

const StyledSubHeading = styled(Heading)`
  &&& {
    font-size: ${props => props.theme.fontSizes.m};
    color: ${props => props.theme.colors.neutralGrey8};
    font-weight: ${props => props.theme.fontWeights.light};
  }
`;

const LogoContainer = styled(Container)`
  padding: 1rem 0 1rem 0;
`;

const StyledContainer = styled.div`
  ${props => props.theme.mediaQueries.tabletMin} {
    margin-left: 0vw;
    max-width: ${props => props.theme.pxToRem(400)};
  }
`;

const StyledButtonContainer = styled.div`
  ${props => props.theme.mediaQueries.ieOnly} {
    margin-top: ${props => props.theme.pxToRem(50)};
  }
`;

const StyledImage = styled.img`
  ${props => props.theme.mediaQueries.mobileOnly} {
    min-height: ${props => props.theme.pxToRem(321)};
  }
`;

const StyledImageContainer = styled(Container)`
  position: relative;
  background: url(${backgroundImage}) no-repeat;
  display: flex;
  height: ${props => props.theme.pxToRem(356)};

  ${props => props.theme.mediaQueries.mobileOnly} {
    padding-top: ${props => props.theme.pxToRem(20)};
    padding-bottom: ${props => props.theme.pxToRem(20)};
    background-size: contain;
  }
  ${props => props.theme.mediaQueries.desktopOnly} {
    z-index: -1;
    left: 50%;
    min-height: 50%;
    background-size: cover;
    min-width: ${props => props.theme.pxToRem(590)};
    ${props => props.theme.mediaQueries.ieOnly} {
      bottom: ${props => props.theme.pxToRem(-20)};
    }
  }
`;

const StyledHeadlineContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: ${props => props.theme.pxToRem(483)};
`;

const StyledParagraph = styled.p`
  display: flex;
  font-size: ${props => props.theme.fontSizes.s};
  font-weight: ${props => props.theme.fontWeights.light};
  line-height: 1.5;
`;

const Headline = styled(Container)`
  position: relative;
  font-size: ${props => props.theme.fontSizes.xxl};
  font-weight: ${props => props.theme.fontWeights.light};
  line-height: ${props => props.theme.pxToRem(50)};
  padding-bottom: ${props => props.theme.pxToRem(40)};
  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.xl};
    line-height: ${props => props.theme.pxToRem(40)};
  }
  ${props => props.theme.mediaQueries.ieOnly} {
    margin-top: ${props => props.theme.pxToRem(-100)};
  }
`;

const SubHeadline = styled.p`
  ${props => `
    font-size: ${props.theme.fontSizes.m};
    font-weight: ${props.theme.fontWeights.light};
    color: ${props.theme.colors.neutralGrey8};
    margin: ${props.theme.pxToRem(20)} 0 ${props.theme.pxToRem(40)};
  `}
`;

const BoldHeadline = styled.span`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.light};
  line-height: ${props => props.theme.pxToRem(40)};
  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.xl};
    line-height: ${props => props.theme.pxToRem(40)};
  }
`;

const StyledHelpContainer = styled(Container)`
  margin-top: ${props => props.theme.pxToRem(50)};
  padding: ${props => props.theme.pxToRem(50)} 0;
  border-top: 1.5px solid ${props => props.theme.colors.neutralGrey3};
`;

const StyledNotificationBanner = styled(NotificationBanner)`
  margin-top: ${props => props.theme.pxToRem(15)};
  margin-bottom: ${props => props.theme.pxToRem(20)};
`;

const StyledIconError = styled(IconError)`
  width: ${props => props.theme.pxToRem(18)};
  height: ${props => props.theme.pxToRem(18)};
  display: inline;
  transform: rotateX(180deg);
`;
